<!-- @format -->

<template>
	<div class="name-disambiguator">
		<div class="name-disambiguator__panel">
			<div class="name-disambiguator__zones">
				<NameDropZone
					:modelValue="firstName"
					variant="bordered"
					:label="$t('§eq_operator.auth_person_first_name')"
					:placeholder="$t('§eq_operator.dnb_disambiguate_drop_here')"
					group="name-parts"
					@update:modelValue="(v) => handleZoneUpdate('firstName', v)"
				/>
				<NameDropZone
					:modelValue="lastName"
					variant="bordered"
					:label="$t('§eq_operator.auth_person_last_name')"
					:placeholder="$t('§eq_operator.dnb_disambiguate_drop_here')"
					group="name-parts"
					@update:modelValue="(v) => handleZoneUpdate('lastName', v)"
				/>
			</div>

			<NameDropZone
				v-if="pool.length > 0"
				:modelValue="pool"
				variant="bare"
				:label="$t('§eq_operator.dnb_disambiguate_pool_label')"
				group="name-parts"
				class="name-disambiguator__pool"
				@update:modelValue="(v) => handleZoneUpdate('pool', v)"
			/>

			<Message
				v-if="needsLastName"
				severity="warn"
				:closable="false"
				class="name-disambiguator__message"
			>
				{{ $t('§eq_operator.dnb_disambiguate_need_last_name') }}
			</Message>

			<Message
				v-else-if="needsToAssignAll"
				severity="warn"
				:closable="false"
				class="name-disambiguator__message"
			>
				{{ $t('§eq_operator.dnb_disambiguate_assign_all_remaining') }}
			</Message>
		</div>

		<div class="name-disambiguator__footer">
			<div class="name-disambiguator__footer-primary">
				<CoreIconButton
					tooltip="§ui_button.back"
					class="mt-1 mr-3"
					color="primary-light"
					icon="arrow-left"
					iconSize="22"
					@buttonClick="handleCancel"
				/>
				<CoreButton
					icon="pi-arrow-right"
					label="§ui_button.proceed"
					:disabled="!canConfirm"
					@click="handleConfirm"
				/>
			</div>
			<div
				v-if="$slots['footer-extra']"
				class="name-disambiguator__footer-extra"
			>
				<slot name="footer-extra" />
			</div>
		</div>
	</div>
</template>

<script>
import Message from 'primevue/message'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreIconButton from '@/components/core/CoreIconButton.vue'
import NameDropZone from './NameDropZone.vue'

function tokenize(fullName) {
	if (typeof fullName !== 'string') return []
	return fullName.split(/\s+/).filter(Boolean)
}

function sameMultiset(a, b) {
	if (a.length !== b.length) return false
	const sortedA = [...a].sort()
	const sortedB = [...b].sort()
	return sortedA.every((t, i) => t === sortedB[i])
}

export default {
	name: 'NameDisambiguator',

	components: {
		Message,
		CoreButton,
		CoreIconButton,
		NameDropZone,
	},

	props: {
		fullName: {
			type: String,
			required: true,
		},
		initial: {
			type: Object,
			default: null,
		},
		requireLastName: {
			type: Boolean,
			default: true,
		},
	},

	emits: ['confirm', 'cancel', 'update:assignment'],

	data() {
		return {
			pool: [],
			firstName: [],
			lastName: [],
		}
	},

	computed: {
		canConfirm() {
			return (
				this.pool.length === 0 &&
				(!this.requireLastName || this.lastName.length > 0)
			)
		},

		needsLastName() {
			return this.requireLastName && this.lastName.length === 0
		},

		needsToAssignAll() {
			return !this.canConfirm && !this.needsLastName
		},
	},

	created() {
		const tokens = tokenize(this.fullName)
		const restored = this.canRestoreInitial(this.initial, tokens)
		if (restored) {
			this.pool = [...this.initial.pool]
			this.firstName = [...this.initial.firstName]
			this.lastName = [...this.initial.lastName]
		} else {
			this.pool = tokens
			this.firstName = []
			this.lastName = []
		}
	},

	methods: {
		handleZoneUpdate(zoneKey, value) {
			this[zoneKey] = value
			this.emitAssignment()
		},

		canRestoreInitial(initial, currentTokens) {
			if (!initial) return false
			if (
				!Array.isArray(initial.pool) ||
				!Array.isArray(initial.firstName) ||
				!Array.isArray(initial.lastName)
			) {
				return false
			}
			const flat = [...initial.pool, ...initial.firstName, ...initial.lastName]
			return sameMultiset(flat, currentTokens)
		},

		emitAssignment() {
			this.$emit('update:assignment', {
				pool: [...this.pool],
				firstName: [...this.firstName],
				lastName: [...this.lastName],
			})
		},

		handleConfirm() {
			if (!this.canConfirm) return
			this.$emit('confirm', {
				first_name: this.firstName.join(' '),
				last_name: this.lastName.join(' '),
				assignment: {
					pool: [...this.pool],
					firstName: [...this.firstName],
					lastName: [...this.lastName],
				},
			})
		},

		handleCancel() {
			this.$emit('cancel')
		},
	},
}
</script>

<style scoped>
.name-disambiguator {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.name-disambiguator__title {
	text-transform: uppercase;
	letter-spacing: 0.08em;
	font-size: 1.1rem;
	font-weight: 600;
	color: var(--color-neutral-900, #1a1f2c);
}

.name-disambiguator__panel {
	background: var(--color-primary-100, #f6fefa);
	border: 1px solid var(--color-primary-300, #defdf3);
	border-radius: 12px;
	padding: 1.75rem 2rem;
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.name-disambiguator__zones {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 2rem;
}

.name-disambiguator__pool {
	margin-top: 0.5rem;
}

.name-disambiguator__message {
	margin: 0;
}

.name-disambiguator__footer {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-top: 0.25rem;
}

.name-disambiguator__footer-primary {
	display: flex;
	gap: 0.75rem;
}

.name-disambiguator__footer-extra {
	margin-left: auto;
	display: flex;
	gap: 0.5rem;
}
</style>
